/**
 * media-gallery.js — FIXED EDITION v3
 *
 * KEY FIXES vs v2:
 * ① The gallery now scrolls ONLY inside its own containers (the desktop
 *    mosaic box or the mobile swipe slider). It never calls scrollIntoView
 *    on the page for variant changes, which is what made the whole page
 *    glide up and then snap back down on mobile when a size/colour was
 *    tapped.
 *
 * ② Variant-driven changes (callers pass { keepPageScroll: true }) jump
 *    instantly to the right slide — no long smooth animation across hidden
 *    slides. Thumbnail clicks keep the original smooth behaviour.
 *
 * ③ Promise-based viewer-ready queue, null-safe thumbnails, local debounce
 *    and the 30ms slideChanged debounce are all kept from v2.
 */

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer:     this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this._controller = new AbortController();

        /* Promise-based viewer-ready: all setActiveMedia calls queue here */
        this._viewerReadyPromise = new Promise((resolve) => {
          this._resolveViewerReady = resolve;
        });
      }

      connectedCallback() {
        const { viewer, thumbnails } = this.elements;
        const signal = this._controller.signal;

        this.stickyHeader = document.querySelector('sticky-header');

        /* Resolve viewer-ready */
        const viewerTag = viewer ? viewer.tagName.toLowerCase() : '';
        const readyPromise = viewerTag.includes('-')
          ? customElements.whenDefined(viewerTag)
          : Promise.resolve();

        readyPromise.then(() => {
          this._resolveViewerReady();
          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        });

        if (!thumbnails) return;

        viewer.addEventListener(
          'slideChanged',
          this._debounce(this.onSlideChanged.bind(this), 30),
          { signal }
        );

        thumbnails.querySelectorAll('[data-target]').forEach((item) => {
          item.querySelector('button')?.addEventListener(
            'click',
            this.setActiveMedia.bind(this, item.dataset.target, false),
            { signal }
          );
        });
      }

      disconnectedCallback() {
        this._controller.abort();
      }

      /* ── Inline debounce ─────────────────────────────────── */
      _debounce(fn, wait) {
        let timer;
        return function(...args) {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), wait);
        };
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      /* ── setActiveMedia: always awaits viewer-ready ────────
         opts.keepPageScroll === true  → variant-driven change: instant,
         gallery-internal scrolling only, the page itself never moves.
         Anything else (e.g. a thumbnail click passes the click event as
         the 3rd argument) keeps the original behaviour. */
      setActiveMedia(mediaId, prepend, opts) {
        this._viewerReadyPromise.then(() => {
          this._setActiveMediaNow(mediaId, prepend, opts);
        });
      }

      _setActiveMediaNow(mediaId, prepend, opts) {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const keepPageScroll = !!(opts && opts.keepPageScroll === true);

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        const previousActive = viewer.querySelector('[data-media-id].is-active');
        if (previousActive && previousActive !== activeMedia) {
          previousActive.classList.remove('is-active');
        }
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail?.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          viewer.slider?.resetPages?.();
        }

        this.preventStickyHeader();

        /* Wait one frame so any show/hide of media (colour filtering) has
           been laid out, then move ONLY the gallery's own scroller. */
        requestAnimationFrame(() => this._revealActiveMedia(activeMedia, keepPageScroll));

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      /* ── Bring the active slide into view WITHOUT touching page scroll ── */
      _revealActiveMedia(activeMedia, keepPageScroll) {
        /* Hidden by colour filtering (display:none) → nothing to reveal */
        if (!activeMedia || activeMedia.offsetParent === null) return;

        const behavior = keepPageScroll ? 'instant' : 'smooth';

        /* Desktop mosaic: the gallery scrolls inside its own bounded box */
        const box = this.querySelector('.gallery-scroll-viewport');
        if (this.mql.matches && box && box.scrollHeight > box.clientHeight + 1) {
          const top =
            box.scrollTop +
            (activeMedia.getBoundingClientRect().top - box.getBoundingClientRect().top);
          box.scrollTo({ top: Math.max(0, top), behavior });
          return;
        }

        /* Mobile swipe slider: horizontal movement only */
        const slider =
          this.elements.viewer?.slider ||
          this.querySelector('[id^="Slider-Gallery"]');
        if (!this.mql.matches && slider && slider.scrollWidth > slider.clientWidth + 1) {
          const sliderRect = slider.getBoundingClientRect();
          const mediaRect = activeMedia.getBoundingClientRect();
          if (mediaRect.width === 0) return;
          const left =
            slider.scrollLeft +
            (mediaRect.left - sliderRect.left) -
            (slider.clientWidth - mediaRect.width) / 2;
          slider.scrollTo({ left: Math.max(0, left), behavior });
          return;
        }

        /* Desktop thumbnail layouts: original Dawn behaviour, but only for
           real thumbnail clicks — never for variant changes. */
        if (!keepPageScroll && this.elements.thumbnails) {
          activeMedia.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button[aria-current]')
          .forEach((btn) => btn.removeAttribute('aria-current'));

        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        const slider = this.elements.thumbnails.slider || this.elements.thumbnails;
        const sliderRect    = slider.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const targetLeft    = slider.scrollLeft + (thumbnailRect.left - sliderRect.left);

        slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          if (!this.elements.liveRegion) return;
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings?.imageAvailable?.replace('[index]', position) || '';
          setTimeout(() => {
            this.elements.liveRegion?.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.onload = announce;
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}
